'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * 指针位置状态
 */
interface PointerState {
  /** 指针 X 坐标百分比 */
  x: number;
  /** 指针 Y 坐标百分比 */
  y: number;
  /** 指针角度（度） */
  angle: number;
  /** 到边缘的距离（0-100） */
  distance: number;
  /** 是否正在动画中 */
  isAnimating: boolean;
}

/**
 * Hook 配置
 */
interface UsePointerTrackingOptions {
  /** 发光灵敏度（0-100），值越小越容易触发发光 */
  glowSensitivity?: number;
}

/**
 * 限制数值在指定范围内
 * @param value - 要限制的值
 * @param min - 最小值
 * @param max - 最大值
 * @returns 限制后的值
 */
const clamp = (value: number, min = 0, max = 100): number => Math.min(Math.max(value, min), max);

/**
 * 四舍五入到指定精度
 * @param value - 要四舍五入的值
 * @param precision - 小数位数
 * @returns 四舍五入后的值
 */
const round = (value: number, precision = 3): number => Number.parseFloat(value.toFixed(precision));

/**
 * 鼠标/指针跟踪 Hook
 * 用于实现发光边框效果
 */
export function usePointerTracking(options: UsePointerTrackingOptions = {}) {
  const { glowSensitivity = 30 } = options;

  const elementRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<PointerState>({
    x: 50,
    y: 50,
    angle: 45,
    distance: 0,
    isAnimating: false,
  });

  /**
   * 获取元素中心点
   */
  const getCenterOfElement = useCallback((el: HTMLElement): [number, number] => {
    const { width, height } = el.getBoundingClientRect();
    return [width / 2, height / 2];
  }, []);

  /**
   * 获取相对于元素的指针位置
   */
  const getPointerPosition = useCallback(
    (
      el: HTMLElement,
      clientX: number,
      clientY: number,
    ): {
      pixels: [number, number];
      percent: [number, number];
    } => {
      const { left, top, width, height } = el.getBoundingClientRect();
      const x = clientX - left;
      const y = clientY - top;
      const px = clamp((100 / width) * x);
      const py = clamp((100 / height) * y);
      return { pixels: [x, y], percent: [px, py] };
    },
    [],
  );

  /**
   * 计算从中心到指针的距离
   */
  const getDistanceFromCenter = useCallback(
    (el: HTMLElement, x: number, y: number): [number, number] => {
      const [cx, cy] = getCenterOfElement(el);
      return [x - cx, y - cy];
    },
    [getCenterOfElement],
  );

  /**
   * 计算指针角度
   */
  const getAngle = useCallback((dx: number, dy: number): number => {
    if (dx === 0 && dy === 0) return 0;

    const angleRadians = Math.atan2(dy, dx);
    let angleDegrees = angleRadians * (180 / Math.PI) + 90;

    if (angleDegrees < 0) {
      angleDegrees += 360;
    }

    return angleDegrees;
  }, []);

  /**
   * 计算到边缘的接近程度
   */
  const getClosenessToEdge = useCallback(
    (el: HTMLElement, x: number, y: number): number => {
      const [cx, cy] = getCenterOfElement(el);
      const [dx, dy] = getDistanceFromCenter(el, x, y);

      let kX = Number.POSITIVE_INFINITY;
      let kY = Number.POSITIVE_INFINITY;

      if (dx !== 0) {
        kX = cx / Math.abs(dx);
      }
      if (dy !== 0) {
        kY = cy / Math.abs(dy);
      }

      return clamp(1 / Math.min(kX, kY), 0, 1);
    },
    [getCenterOfElement, getDistanceFromCenter],
  );

  /**
   * 处理指针移动
   */
  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const el = elementRef.current;
      if (!el) return;

      const position = getPointerPosition(el, e.clientX, e.clientY);
      const [px, py] = position.pixels;
      const [percentX, percentY] = position.percent;
      const [dx, dy] = getDistanceFromCenter(el, px, py);
      const edge = getClosenessToEdge(el, px, py);
      const angle = getAngle(dx, dy);

      setState({
        x: round(percentX),
        y: round(percentY),
        angle: round(angle),
        distance: round(edge * 100),
        isAnimating: false,
      });
    },
    [getPointerPosition, getDistanceFromCenter, getClosenessToEdge, getAngle],
  );

  /**
   * 绑定事件监听器
   */
  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    el.addEventListener('pointermove', handlePointerMove);

    return () => {
      el.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove]);

  const cssVariables = useMemo(
    () =>
      ({
        '--pointer-x': `${state.x}%`,
        '--pointer-y': `${state.y}%`,
        '--pointer-angle': `${state.angle}deg`,
        '--pointer-d': String(state.distance),
        '--glow-sens': String(glowSensitivity),
      }) as React.CSSProperties,
    [state.x, state.y, state.angle, state.distance, glowSensitivity],
  );

  return {
    ref: elementRef,
    state,
    cssVariables,
    isAnimating: state.isAnimating,
  };
}
